import { Box, Button, Card, CardHeader, Container, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { Budget } from "../../components/dashboard/budget";
import { TasksProgress } from "../../components/dashboard/tasks-progress";
import { TotalCustomers } from "../../components/dashboard/total-customers";
import { TotalProfit } from "../../components/dashboard/total-profit";
import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import NewSalesChart from "./NewSalesChart";

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function Dashboard() {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [isGuideOpen, setIsGuideOpen] = useState(false);

    // 🔥 MẶC ĐỊNH KHI VÀO: Tất cả từ trước đến giờ
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const fetchStatistic = async (start, end) => {
        setLoading(true);
        try {
            let s = null;
            let e = null;

            if (start) {
                s = new Date(start);
                s.setHours(0, 0, 0, 0);
            }
            if (end) {
                e = new Date(end);
                e.setHours(23, 59, 59, 999);
            }

            const params = {
                dateStart: s ? s.toISOString() : null,
                dateEnd: e ? e.toISOString() : null
            };

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/statistic/getalladmin`, { params });

            if (res.data.success) {
                setData(res.data.data);
            }
            setLoading(false);
        } catch (err) {
            console.error("Lỗi API thống kê:", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((startDate && endDate) || (!startDate && !endDate)) {
            fetchStatistic(startDate, endDate);
        }
    }, [startDate, endDate]);

    const handleSetAllTime = () => {
        setDateRange([null, null]);
    };

    const handleSetToday = () => {
        const today = new Date();
        setDateRange([today, today]);
    };

    const handleSetYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setDateRange([yesterday, yesterday]);
    };

    const handleSetThisWeek = () => {
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(now.setDate(diff));
        setDateRange([monday, new Date()]);
    };

    const handleSetThisMonth = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        setDateRange([firstDay, new Date()]);
    };

    const handleSetLastMonth = () => {
        const now = new Date();
        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        setDateRange([firstDayLastMonth, lastDayLastMonth]);
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
                    {isLoading && (
                        <div className="loading" style={{ maxWidth: "100%", zIndex: 9999 }}>
                            <div className="loader"></div>
                        </div>
                    )}

                    <Container maxWidth={false}>
                        {/* Dashboard Title & Guide Button */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: "#FF5722", fontSize: "28px" }}>
                                Dashboard
                            </Typography>
                            <button className="ax-guide-button" onClick={() => setIsGuideOpen(true)}>
                                Hướng dẫn <span>❓</span>
                            </button>
                        </Box>

                        {/* Top Filter Block with Quick Filters */}
                        <div className="ax-filter-block" style={{ marginBottom: "24px" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "14px", fontWeight: 700, color: "#555" }}>● Khoảng thời gian</span>
                                    <div className="ax-date-range">
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(update) => setDateRange(update)}
                                            maxDate={new Date()}
                                            isClearable={true}
                                            placeholderText="Tất cả từ trước đến giờ..."
                                            dateFormat="yyyy-MM-dd HH:mm:ss"
                                            customInput={
                                                <input
                                                    style={{
                                                        height: "38px",
                                                        padding: "0 14px",
                                                        borderRadius: "8px",
                                                        border: "1px solid #dcdfe6",
                                                        fontSize: "13px",
                                                        color: "#333",
                                                        minWidth: "280px",
                                                        outline: "none",
                                                        background: "#fff"
                                                    }}
                                                />
                                            }
                                        />
                                    </div>

                                    {/* 6 Quick Filter Buttons */}
                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginLeft: "10px" }}>
                                        <button type="button" onClick={handleSetAllTime} style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px", border: "1px solid #8c8c8c", background: "#f5f5f5", color: "#595959", fontWeight: 600, cursor: "pointer" }}>Tất cả ngày</button>
                                        <button type="button" onClick={handleSetToday} style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px", border: "1px solid #1890ff", background: "#e6f7ff", color: "#1890ff", fontWeight: 600, cursor: "pointer" }}>Hôm nay</button>
                                        <button type="button" onClick={handleSetYesterday} style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px", border: "1px solid #fa541c", background: "#fff2e8", color: "#fa541c", fontWeight: 600, cursor: "pointer" }}>Hôm qua</button>
                                        <button type="button" onClick={handleSetThisWeek} style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px", border: "1px solid #722ed1", background: "#f9f0ff", color: "#722ed1", fontWeight: 600, cursor: "pointer" }}>Tuần này</button>
                                        <button type="button" onClick={handleSetThisMonth} style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px", border: "1px solid #52c41a", background: "#f6ffed", color: "#52c41a", fontWeight: 600, cursor: "pointer" }}>Tháng này</button>
                                        <button type="button" onClick={handleSetLastMonth} style={{ padding: "6px 12px", fontSize: "12px", borderRadius: "6px", border: "1px solid #fa8c16", background: "#fff7e6", color: "#fa8c16", fontWeight: 600, cursor: "pointer" }}>Tháng trước</button>
                                    </div>
                                </div>

                                <button
                                    className="ax-btn-search-red"
                                    onClick={() => fetchStatistic(startDate, endDate)}
                                    style={{ background: "linear-gradient(135deg, #FF5722 0%, #E65100 100%)", borderRadius: "8px", height: "40px", padding: "0 28px" }}
                                >
                                    Tìm kiếm
                                </button>
                            </div>
                        </div>

                        {/* 15 Gradient Cards Grid (Matching happy.ax68win.com Screenshot) */}
                        <div className="ax-dash-grid">
                            {/* Card 1 */}
                            <div className="ax-grad-card g1">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">💰</div>
                                    <div className="ax-card-badge">📈</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">SỐ ĐIỂM HỆ THỐNG</div>
                                    <div className="ax-card-val">{data?.sodiemhethong ? Number(data.sodiemhethong).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill"></div></div>
                            </div>

                            {/* Card 2 */}
                            <div className="ax-grad-card g2">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">👥</div>
                                    <div className="ax-card-badge">👤</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THÀNH VIÊN</div>
                                    <div className="ax-card-val">{data?.thanhvien ? Number(data.thanhvien).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "75%" }}></div></div>
                            </div>

                            {/* Card 3 */}
                            <div className="ax-grad-card g3">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🆕</div>
                                    <div className="ax-card-badge">✨</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TV ĐĂNG KÝ HÔM NAY</div>
                                    <div className="ax-card-val">{data?.userToday ? Number(data.userToday).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "30%" }}></div></div>
                            </div>

                            {/* Card 4 */}
                            <div className="ax-grad-card g4">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">💳</div>
                                    <div className="ax-card-badge">⬆️</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG NẠP</div>
                                    <div className="ax-card-val">{data?.tongnaps ? Number(data.tongnaps).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "65%" }}></div></div>
                            </div>

                            {/* Card 5 */}
                            <div className="ax-grad-card g5">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">💸</div>
                                    <div className="ax-card-badge">⬇️</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG RÚT</div>
                                    <div className="ax-card-val">{data?.tongruts ? Number(data.tongruts).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "40%" }}></div></div>
                            </div>

                            {/* Card 6 */}
                            <div className="ax-grad-card g6">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">👑</div>
                                    <div className="ax-card-badge">⬆️</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG NẠP BỞI ADMIN</div>
                                    <div className="ax-card-val">{data?.tongnapadmin ? Number(data.tongnapadmin).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>

                            {/* Card 7 */}
                            <div className="ax-grad-card g7">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🔱</div>
                                    <div className="ax-card-badge">⬇️</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG RÚT BỞI ADMIN</div>
                                    <div className="ax-card-val">{data?.tongrutadmin ? Number(data.tongrutadmin).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "10%" }}></div></div>
                            </div>

                            {/* Card 8 */}
                            <div className="ax-grad-card g8">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🔄</div>
                                    <div className="ax-card-badge">🔮</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">HOÀN TRẢ</div>
                                    <div className="ax-card-val">{data?.hoantra ? Number(data.hoantra).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "55%" }}></div></div>
                            </div>

                            {/* Card 9 */}
                            <div className="ax-grad-card g9">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🎁</div>
                                    <div className="ax-card-badge">💥</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">KHUYẾN MÃI</div>
                                    <div className="ax-card-val">{data?.khuyenmai ? Number(data.khuyenmai).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "15%" }}></div></div>
                            </div>

                            {/* Card 10 */}
                            <div className="ax-grad-card g10">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🎰</div>
                                    <div className="ax-card-badge">🎲</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG CƯỢC GAME API</div>
                                    <div className="ax-card-val">{data?.tongcuocapi ? Number(data.tongcuocapi).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "60%" }}></div></div>
                            </div>

                            {/* Card 11 */}
                            <div className="ax-grad-card g11">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🏆</div>
                                    <div className="ax-card-badge">🎉</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THẮNG GAME API</div>
                                    <div className="ax-card-val">{data?.tongthangapi ? Number(data.tongthangapi).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>

                            {/* Card 12 */}
                            <div className="ax-grad-card g12">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">📉</div>
                                    <div className="ax-card-badge">🤢</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THUA GAME API</div>
                                    <div className="ax-card-val">{data?.tongthuaapi ? Number(data.tongthuaapi).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "45%" }}></div></div>
                            </div>

                            {/* Card 13 */}
                            <div className="ax-grad-card g13">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🎮</div>
                                    <div className="ax-card-badge">🕹️</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG CƯỢC MINI GAME</div>
                                    <div className="ax-card-val">{data?.tongcuocminigame ? Number(data.tongcuocminigame).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "80%" }}></div></div>
                            </div>

                            {/* Card 14 */}
                            <div className="ax-grad-card g14">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">🏅</div>
                                    <div className="ax-card-badge">🎊</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THẮNG MINI GAME</div>
                                    <div className="ax-card-val">{data?.tongthangminigame ? Number(data.tongthangminigame).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "55%" }}></div></div>
                            </div>

                            {/* Card 15 */}
                            <div className="ax-grad-card g15">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box">📊</div>
                                    <div className="ax-card-badge">📋</div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THUA MINI GAME</div>
                                    <div className="ax-card-val">{data?.tongthuaminigame ? Number(data.tongthuaminigame).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>
                        </div>
                    </Container>
                </Box>

                {/* AX68WIN HƯỚNG DẪN MODAL */}
                {isGuideOpen && (
                    <div className="ax-modal-overlay" onClick={() => setIsGuideOpen(false)}>
                        <div className="ax-modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="ax-modal-header">
                                <h3>Hướng dẫn chi tiết Dashboard</h3>
                                <span className="close-icon" onClick={() => setIsGuideOpen(false)}>✕</span>
                            </div>
                            <div className="ax-modal-body">
                                <h4>Khoảng thời gian</h4>
                                <ul>
                                    <li>Được sử dụng để tra cứu thống kê số liệu tổng hợp hệ thống theo mốc thời gian tùy chọn.</li>
                                </ul>

                                <h4>Các chỉ số hệ thống</h4>
                                <ul>
                                    <li>Hiển thị chi tiết 15 chỉ số quan trọng: Số điểm hệ thống, Tổng thành viên, TV đăng ký hôm nay, Tổng nạp, Tổng rút, Nạp/Rút Admin, Hoàn trả, Khuyến mãi, và thống kê cược/thắng/thua chi tiết của Game API & Mini Game.</li>
                                </ul>
                            </div>
                            <div className="ax-modal-footer">
                                <button className="ax-btn-modal-close" onClick={() => setIsGuideOpen(false)}>
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Dashboard;